import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { History, Users, Calendar, Clock, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { reservationApi } from '../../services/reservationApi';
import type { MergeGroup } from '../../context/MergeGroupContext';
import type { BookingStatus } from '../../types/reservations';

interface MergeHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMergeGroupsUpdated: () => void;
}

interface HistoricalMergeGroup extends MergeGroup {
  date: string;
  time: string;
  username: string;
  status: BookingStatus;
  reservationId: number;
}

const MergeHistoryDialog: React.FC<MergeHistoryDialogProps> = ({
  open,
  onOpenChange,
  onMergeGroupsUpdated,
}) => {
  const [historicalGroups, setHistoricalGroups] = useState<HistoricalMergeGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<'today' | 'week' | 'month' | 'year'>('week');
  
  // Load historical merge groups
  const loadHistoricalMergeGroups = React.useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      let startDate = today;
      switch (selectedDateRange) {
        case 'today': {
          // already set to today
          break;
        }
        case 'week': {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          startDate = weekAgo.toISOString().split('T')[0];
          break;
        }
        case 'month': {
          const monthAgo = new Date();
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          startDate = monthAgo.toISOString().split('T')[0];
          break;
        }
        case 'year': {
          const yearAgo = new Date();
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          startDate = yearAgo.toISOString().split('T')[0];
          break;
        }
      }

      // Add timeout protection (15 seconds for potentially large datasets)
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out. Try a shorter time range.')), 15000)
      );

      // Get reservations with merge groups
      const reservationPromise = reservationApi.searchReservationsByPeriodWithParams(startDate, selectedDateRange);
      const reservations = await Promise.race([reservationPromise, timeoutPromise]);
      
      // Filter reservations that have merge groups
      const mergeGroupReservations = reservations.filter(r => 
        r.restaurantTableId && 
        r.restaurantTableId.length > 1 && 
        'mergeGroupName' in r && r.mergeGroupName // Backend includes merge group info
      );

      // Convert to historical merge groups
      const groups: HistoricalMergeGroup[] = mergeGroupReservations.map(reservation => {
        const extendedReservation = reservation as typeof reservation & { 
          mergeGroupName?: string; 
          mergeGroupColor?: string; 
        };
        return {
          id: `history_${reservation.id}`,
          name: extendedReservation.mergeGroupName || `Merged Group ${reservation.id}`,
          tableIds: reservation.restaurantTableId?.map(t => t.id.toString()) || [],
          totalCapacity: reservation.guestNo || 0,
          restaurantTableId: reservation.restaurantTableId || [],
          color: extendedReservation.mergeGroupColor || '#10B981',
          date: reservation.date,
          time: reservation.time,
          username: reservation.username,
          status: reservation.status,
          reservationId: reservation.id || 0
        };
      });

      setHistoricalGroups(groups);
    } catch (error) {
      //console.error('Failed to load historical merge groups:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load merge history';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedDateRange]);

  // Load data when dialog opens or date range changes
  useEffect(() => {
    if (open) {
      loadHistoricalMergeGroups();
    }
  }, [open, selectedDateRange, loadHistoricalMergeGroups]);

  // Unmerge a historical group
  const handleUnmergeGroup = async (group: HistoricalMergeGroup) => {
    try {
      const { BookingStatus } = await import('../../types/reservations');
      
      //console.log('🔄 Attempting to unmerge group:', group.reservationId, 'with status:', BookingStatus.CANCELED);
      
      // Call backend API to unmerge/cancel the group
      await reservationApi.deleteMergeGroup(group.reservationId, BookingStatus.CANCELED);
      
      toast.success(`Unmerged group "${group.name}" successfully`);
      
      // Refresh the list
      await loadHistoricalMergeGroups();
      
      // Notify parent to refresh current merge groups
      onMergeGroupsUpdated();
      
    } catch (error) {
      //console.error('❌ Failed to unmerge group:', error);
      //console.error('Group details:', group);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to unmerge group`);
    }
  };

  const getStatusBadgeVariant = (status: BookingStatus) => {
    switch (status) {
      case 'Accepted': return 'default' as const;
      case 'Pending': return 'secondary' as const;
      case 'Rejected': return 'destructive' as const;
      case 'Canceled': return 'outline' as const;
      case 'Completed': return 'default' as const;
      default: return 'outline' as const;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white dark:bg-gray-900 sm:max-w-[800px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-purple-600" />
            Merge Group History
          </DialogTitle>
          <DialogDescription>
            View and manage historical table merge groups. You can unmerge groups to make tables available again.
          </DialogDescription>
        </DialogHeader>

        {/* Date Range Filter */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm font-medium">Show:</span>
          {(['today', 'week', 'month', 'year'] as const).map((range) => (
            <Button
              key={range}
              variant={selectedDateRange === range ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedDateRange(range)}
            >
              {range === 'today' ? 'Today' : 
               range === 'week' ? 'Past Week' :
               range === 'month' ? 'Past Month' : 'Past Year'}
            </Button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading merge history...</span>
            </div>
          ) : historicalGroups.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No merge groups found for the selected period</p>
            </div>
          ) : (
            historicalGroups.map((group) => (
              <Card key={group.id} className="border-l-4" style={{ borderLeftColor: group.color }}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: group.color }}
                      />
                      {group.name}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant={getStatusBadgeVariant(group.status)}>
                        {group.status}
                      </Badge>
                      {group.status === 'Accepted' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnmergeGroup(group)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Unmerge
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span>{group.totalCapacity} guests total</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span>{group.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span>{group.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Customer:</span>
                        <span className="font-medium">{group.username}</span>
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        Merged Tables ({group.restaurantTableId?.length || 0}):
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {group.restaurantTableId?.map((table) => (
                          <Badge key={table.id} variant="outline" className="text-xs">
                            {table.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  {group.status === 'Canceled' && (
                    <div className="mt-3 p-2 bg-gray-50 rounded-lg flex items-center gap-2 text-sm text-gray-600">
                      <AlertTriangle className="w-4 h-4" />
                      This merge group has been unmerged
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={loadHistoricalMergeGroups} disabled={loading}>
            <History className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeHistoryDialog;